import { defineCopy } from '@/copy';

/** players strings. Keep the voice: short, funny, no em dashes. */
export const copy = defineCopy('players', {
  crew: {
    title: 'Crew',
    /** {n} players, {m} with the app. */
    count: '{players}, {m} on the app.',
    countOne: 'Crew of one. Invite people.',
    searchLabel: 'Search the crew',
    searchPlaceholder: 'Name or nickname, Greek or Latin',
    sortLabel: 'Sort by',
    sort: { ovr: 'OVR', name: 'Name', recent: 'Newest' },
    filterLabel: 'Filter the crew',
    filter: { all: 'All', members: 'Members', guests: 'Guests', rookies: 'Rookies' },
    invite: 'Invite',
    addGuest: 'Add guest',
    loading: 'Counting heads...',
    loadError: "Couldn't load the crew. Pull down in a sec.",
    empty: {
      title: 'Crew of one. Invite people.',
      body: 'A roster needs at least two guys who can miss.',
    },
    noMatch: {
      title: 'Nobody by that name',
      body: 'Check the spelling. Greek and Latin both work.',
      clear: 'Clear search',
    },
    noRookies: { title: 'No rookies left', body: 'Everyone has a card. Respect.' },
    noGuests: { title: 'No guests', body: 'Everyone brought a phone.' },
    tag: { guest: 'Guest', owner: 'Owner', here: 'Here' },
    openPlayer: 'Open {name}',
  },

  homeInvite: {
    title: 'Crew of one. Send the code.',
    line: 'Nobody else is in yet. Share the crew code and they can join from their own home screen.',
    cta: 'Invite the crew',
  },

  guest: {
    title: 'Add a guest',
    line: 'A cousin with no phone. They play, the stats are kept, and they can claim them later.',
    nameLabel: 'Name',
    namePlaceholder: 'Guest name',
    nameHint: 'Greek or Latin letters, your call.',
    nicknameLabel: 'Nickname',
    nicknameHint: 'Optional. What the crew yells.',
    save: 'Add guest',
    saving: 'Adding',
    added: '{name} is on the roster.',
    queued: '{name} is saved on this phone and joins the crew when you get signal.',
    failed: "Couldn't add them. Try again.",
    needName: 'Give the guest a name, even a bad one.',
    add: 'Add a guest',
    addShort: 'Add guest',
    full: 'Lineup is full. Guest is on the roster, not in the game.',
  },

  invite: {
    title: 'Invite',
    line: 'Share the code. Bring someone who can actually shoot.',
    crewCode: 'Crew code',
    codeLabel: 'Crew code {code}',
    qrLabel: 'QR code for the crew link',
    qrHint: 'Point a camera at it. It opens the app link.',
    copyCode: 'Copy code',
    copyLink: 'Copy link',
    share: 'Share',
    copied: 'Copied.',
    copyFailed: "Couldn't copy. Long press the code instead.",
    shareText: 'Join {crew} on TTS Basketball. Install the app, then use code {code}.',
    shareTitle: 'Join {crew}',
    noExpiry: 'No expiry. Joined phones stay in if you rotate it.',
    expires: 'Expires {date}.',
    rotate: 'Rotate code',
    rotateTitle: 'Rotate the crew code?',
    rotateBody: 'The old code stops working. Phones that already joined stay in.',
    rotateConfirm: 'Rotate it',
    rotated: 'New code. The old one is dead.',
    rotateFailed: "Couldn't rotate it. Try again.",
    ownerOnly: 'Only the owner can rotate it.',
    loadError: "Couldn't load the code. Check your signal.",
    personal: {
      title: 'Already on the roster?',
      line: 'Guests who got a phone can take over their own stats. One-use code, 14 days.',
      empty: 'No guests waiting on a phone.',
      action: 'Invite',
      again: 'New code',
      pending: 'Pending',
      revoke: 'Revoke',
      revoked: 'Invite revoked.',
      sheetTitle: 'Invite {name}',
      sheetLine: 'One use, 14 days. Their stats land on their card.',
      sheetNote: 'This code is shown once. Close this and it is gone.',
      failed: "Couldn't make the invite. Try again.",
      already: '{name} already has the app.',
    },
    relogin: {
      title: 'Locked out?',
      line: 'New phone, reinstall, cleared data. Get a crewmate back in with a six character code.',
      action: 'Get them back in',
      empty: 'Nobody else is on the crew yet.',
    },
  },

  relogin: {
    title: 'Get {name} back in',
    line: 'Tell them this code. It works once, for 15 minutes.',
    steps: 'On their phone: open the app, tap "Use a comeback code", type this in.',
    codeLabel: 'Relogin code {code}',
    expires: 'Valid until {time}.',
    copy: 'Copy code',
    copied: 'Copied.',
    failed: "Couldn't make a code. Try again.",
    notMember: '{name} does not have the app yet.',
    close: 'Done',
  },

  profile: {
    title: 'Player',
    loading: 'Warming up...',
    notFound: { title: 'No such player', body: 'Maybe they retired. Try the crew list.', action: 'Back to the crew' },
    loadError: "Couldn't load this player.",
    edit: 'Edit card',
    challenge: 'Challenge',
    makeTheirCard: 'Make their card',
    invite: 'Invite to the app',
    relogin: 'Get them back in',
    you: 'You',
    tag: { guest: 'Guest', owner: 'Owner', retired: 'Retired', loginOff: 'Login off', here: 'Here today', you: 'You' },
    facts: {
      jersey: 'Jersey',
      height: 'Height',
      hand: 'Hand',
      joined: 'In the crew since',
      unset: 'Not set',
      cm: '{n} cm',
      hands: { L: 'Lefty', R: 'Righty' },
    },
    career: {
      title: 'Career',
      gp: 'Games',
      record: 'W-L',
      winPct: 'Win %',
      streak: 'Streak',
      streakWin: '{n}W',
      best: 'Best {n}',
      none: 'No games logged. Suspicious.',
      unavailable: "Career stats aren't in yet.",
    },
    ratings: {
      title: 'Ratings',
      ovr: 'Overall',
      unrated: 'Unrated until the combine is done.',
      provisional: 'Provisional',
      rusty: 'Rusty',
      attrsTitle: 'Attributes',
      combine: 'Combine done',
      combinePending: 'Combine pending',
    },
    recent: {
      title: 'Recent games',
      empty: 'No games logged. Suspicious.',
      unavailable: "Game log isn't in yet.",
      win: 'W',
      loss: 'L',
      draw: 'D',
      row: '{mode} · {a} to {b}',
      winLabel: 'Won',
      lossLabel: 'Lost',
    },
    privacy: {
      title: 'AI cards',
      label: 'Let the crew make my AI card',
      labelOther: 'Let the crew make their AI card',
      help: 'Off by default. You can always hide or delete anything made of you.',
      helpOther: 'They have no phone, so the owner says yes for them. Ask first.',
      saved: 'Saved.',
      failed: "Couldn't save that. Try again.",
    },
    owner: {
      title: 'Roster',
      retire: 'Retire from the roster',
      retired: 'Retired. Pickers skip them now.',
      retireBlocked: 'They still have the app. Disable them in Admin first.',
      bringBack: 'Bring back',
      broughtBack: 'Back on the roster.',
      failed: "Couldn't do that. Try again.",
    },
    sections: { extra: 'More' },
  },

  edit: {
    title: 'Edit card',
    save: 'Save',
    saving: 'Saving',
    cancel: 'Cancel',
    name: 'Name',
    nameHint: 'Greek or Latin letters, your call.',
    nickname: 'Nickname',
    nicknameHint: 'What the crew yells. Leave it empty for none.',
    jersey: 'Jersey number',
    jerseyHint: '0 to 99. Double zero is just zero.',
    height: 'Height in cm',
    heightHint: '100 to 250. Be honest, the court is not.',
    hand: 'Shooting hand',
    handOptions: { R: 'Righty', L: 'Lefty', none: 'Skip' },
    errors: {
      name: 'A name between 1 and 40 letters, please.',
      nickname: 'Nicknames max out at 24.',
      jersey: 'A whole number from 0 to 99.',
      height: 'A whole number from 100 to 250.',
    },
    saved: 'Card updated.',
    nothing: 'Nothing changed.',
    failed: "Couldn't save. Try again.",
    forbidden: 'Only they can edit their own card.',
  },

  admin: {
    title: 'Crew and invites',
    crewTitle: 'The crew',
    crewName: 'Crew name',
    crewNameHint: 'Shows on the invite and on shared cards.',
    saveName: 'Save name',
    nameSaved: 'Crew renamed.',
    membersTitle: 'Members',
    membersLine: 'Roles and logins. Tap a name.',
    memberRow: { owner: 'Owner', member: 'Member', disabled: 'Disabled', you: 'You' },
    empty: 'No members yet. Odd.',
    loadError: "Couldn't load the members.",
    actionsTitle: '{name}',
    makeOwner: 'Make owner',
    makeMember: 'Make member',
    disable: 'Disable login',
    enable: 'Enable login',
    relogin: 'Get them back in',
    openProfile: 'Open profile',
    roleChanged: '{name} is now {role}.',
    disabledToast: '{name} is signed out everywhere.',
    enabledToast: '{name} can come back in with a relogin code.',
    lastOwner: 'A crew needs an owner. Make someone else the owner first.',
    failed: "Couldn't do that. Try again.",
    disableTitle: 'Disable {name}?',
    disableBody: 'Every phone of theirs signs out. Their stats stay.',
    disableConfirm: 'Disable',
    inviteTitle: 'Crew code',
    inviteLine: 'Rotate it when it leaks. Phones already in stay in.',
    openInvite: 'Invite screen',
    pendingTitle: 'Pending personal invites',
    pendingEmpty: 'None pending.',
    pendingRow: 'Expires {date}',
    retiredTitle: 'Retired players',
    retiredEmpty: 'Nobody retired.',
    bringBack: 'Bring back',
    broughtBack: '{name} is back on the roster.',
  },

  me: {
    title: 'Your card',
    line: 'Name, jersey, height and the AI card switch.',
    open: 'Open my profile',
    edit: 'Edit card',
  },

  picker: {
    guestPlaceholder: 'Guest name',
    guestAdd: 'Add guest',
    guestLabel: 'Add a guest',
    searchLabel: 'Search players',
    searchPlaceholder: 'Search',
    empty: 'Nobody on the roster yet.',
    noMatch: 'Nobody by that name.',
    here: 'Here',
    guestTag: 'Guest',
    max: 'Lineup is full ({n}).',
    selected: '{n} picked',
    pickOrder: 'Pick {n}',
    guestQueued: '{name} is saved on this phone and syncs later.',
    loading: 'Loading the crew...',
  },
});
